import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-ui";
import { ToolPencilLineIcon } from "@skbkontur/icons";

import { emptyRef } from "../../TypingUtils";
import { useAsyncEffect } from "../../../../../Common/UseAsyncEffect";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import {
    FIAS_CLOSE_MODAL_WITH_SAVE,
    FIAS_CLOSE_MODAL_WITHOUT_SAVE,
    FIAS_ENTER_EDIT_MODE,
    FIAS_OUT_EDIT_MODE,
    FIAS_SAVE_MODAL_CUSTOM_ADDRESS,
    FIAS_SAVE_VIA_WIDGET,
} from "../../metricsAction";
import { AbsoluteResolvedModelPath, emptyAbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { Text } from "../../ValueViewers/Text/Text";
import { isNotNullOrEmpty, isNullOrEmpty } from "../../../../../Common/TypingUtils";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { getCandyHub } from "../../../Forms/Sentry/InitializeSentry";
import { runFloatingAsyncAction } from "../../../Helpers/TypingUtils";

import { FiasApi, maxItems } from "./FiasInput/FiasApi";
import { addressLevels, divisionType, FiasAddressFields, ParsedExtendedFiasField } from "./FiasInput/Types/FiasTypes";
import {
    checkAddressExists,
    dirtyReduxHack,
    extractStringAddress,
    FiasAddress,
    fiasHasOnlyNotRequiredPathFields,
    fiasUnknownGuidMessage,
    fiasWarningMessage,
    isAnyAddressByPathExists,
} from "./Utils/FiasAddressUtils";
import { FiasModalEditor } from "./FiasEditor/FiasModal";
import { AdrGarOptions, FnsOptions, MvdOptions } from "./FiasContainer";
import { FiasInput } from "./FiasInput/FiasInput";
import { convertFiasAddressToModelAddress } from "./Utils/FiasConversions";
import { WarningText } from "./FiasInput/Fias.styles";
import { Root } from "./Fias.styles";
import { FiasStringAddress } from "./FiasStringAddress";
import { FiasTestPageObject, FiasTestPageObjectType } from "./FiasTestPageObject";
import { splitExtendedFieldsForStringAddress } from "./FiasInput/Utils/utils";
import { FiasWidgetContainer as FiasWidget } from "./FiasEditor/FiasWidget/FiasWidgetContainer";

export interface FiasProps extends ControlValidationProps, ControlTooltipProps {
    bindingPath: AbsoluteResolvedModelPath;
    value: FiasAddress;
    onValueChange: (nextValue: FiasAddress) => void;
    title: string;
    baseUrl: string;
    focusByEnterOnNextControl: (event: React.KeyboardEvent) => void;
    withForeignCountry: boolean;
    withSteads: boolean;
    division: divisionType;
    extendedFields: ParsedExtendedFiasField[];
    searchToAddressLevel: addressLevels;
    pathToFullAddress?: string;
    clearSubstring?: string;
    fnsOptions?: FnsOptions;
    mvdOptions?: MvdOptions;
    adrGarOptions?: AdrGarOptions;
    isReadOnly?: boolean;
    onClick?: () => void;
    isLink?: boolean;
    onlyInput?: boolean;
    hasOwnBindingPath: boolean;
}

export function Fias(props: FiasProps): JSX.Element {
    const {
        bindingPath,
        value,
        onValueChange,
        baseUrl,
        focusByEnterOnNextControl,
        title,
        withForeignCountry,
        withSteads,
        division,
        extendedFields,
        searchToAddressLevel,
        pathToFullAddress,
        fnsOptions,
        mvdOptions,
        onClick,
        isLink,
        isReadOnly,
        onlyInput,
        error,
        warning,
        hasOwnBindingPath,
    } = props;

    const [modalEditorOpened, setModalEditorOpened] = useState<boolean>(false);
    const [focusOnOpen, setFocusOnOpen] = useState<boolean>(false);
    const [mode, setMode] = useState<"edit" | "show">("show");
    const [isInFocus, setIsInFocus] = useState<boolean>(false);
    const [stringAddress, setStringAddress] = useState<string>("");
    const [internalWarning, setInternalWarning] = useState<string>("");
    const container = useRef<HTMLDivElement>(emptyRef);
    const apiClient = useRef<FiasApi>(new FiasApi(baseUrl, maxItems, division, searchToAddressLevel, withSteads));
    const track = useMetrics();

    const { notRequiredExtendedFields, requiredExtendedFields } = useMemo(
        () =>
            fnsOptions?.extendModal
                ? splitExtendedFieldsForStringAddress(extendedFields)
                : { notRequiredExtendedFields: [], requiredExtendedFields: extendedFields },
        [fnsOptions, extendedFields]
    );

    const [fiasInputMethods, setFiasInputMethods] = useState<FiasTestPageObjectType>();

    useAsyncEffect(
        async () => {
            if (pathToFullAddress) {
                onValueChange({ ...value, [pathToFullAddress]: stringAddress });
            }

            let fiasWarning =
                !isAnyAddressByPathExists(value, requiredExtendedFields) && isNotNullOrEmpty(stringAddress)
                    ? fiasWarningMessage
                    : "";
            if (fnsOptions?.extendModal && isNullOrEmpty(fiasWarning) && isNullOrEmpty(stringAddress)) {
                fiasWarning = isAnyAddressByPathExists(value, notRequiredExtendedFields)
                    ? fiasHasOnlyNotRequiredPathFields
                    : "";
            }
            setInternalWarning(fiasWarning);
        },
        dirtyReduxHack(value, { stringAddress: stringAddress })
    );

    useAsyncEffect(async () => {
        if (!isInFocus) {
            // изменения адреса из внешних источников - СС / очистка раздела
            await setNextStringAddress(value);
        }
    }, dirtyReduxHack(value));

    useAsyncEffect(async () => {
        await setNextStringAddress(value);
    }, []);

    useEffect(() => {
        if (onlyInput) {
            setMode("edit");
        }
    }, [mode]);

    const setNextStringAddress = async (nextValue: FiasAddress) => {
        try {
            const extractedStringAddress = await extractStringAddress(
                nextValue,
                requiredExtendedFields,
                apiClient.current
            );
            setStringAddress(extractedStringAddress);
            setMode(extractedStringAddress ? "show" : "edit");
        } catch {
            setStringAddress("");
            setMode("edit");
            setInternalWarning(fiasUnknownGuidMessage);
        }
    };

    const setEditModeCallback = React.useCallback(() => {
        setFocusOnOpen(true);
        setMode("edit");
        track(FIAS_ENTER_EDIT_MODE, {
            withEmptyAddress: isNullOrEmpty(stringAddress),
        });
    }, [stringAddress]);

    const onBlurHandler = () => {
        if (stringAddress) {
            setMode("show");
        }
        track(FIAS_OUT_EDIT_MODE, {
            withEmptyAddress: isNullOrEmpty(stringAddress),
        });
    };

    const handleCloseModalEditor = () => {
        setModalEditorOpened(false);
        track(FIAS_CLOSE_MODAL_WITHOUT_SAVE);
    };

    const onApplyHandler = async (nextValue: FiasAddress) => {
        track(FIAS_CLOSE_MODAL_WITH_SAVE);
        setModalEditorOpened(false);
        onValueChange(nextValue);
        await setNextStringAddress(nextValue);
        trackModalAddressSaved(nextValue);
    };

    const onSaveViaWidget = (nextValue: FiasAddress) => {
        track(FIAS_SAVE_VIA_WIDGET);
        onValueChange(nextValue);
    };

    const submitHandler = async (address: FiasAddressFields) => {
        const nextValue = convertFiasAddressToModelAddress(address, extendedFields);
        onValueChange(nextValue);
        try {
            const extractedStringAddress = await extractStringAddress(
                nextValue,
                requiredExtendedFields,
                apiClient.current
            );
            setStringAddress(extractedStringAddress);
        } catch (e) {
            // Просто оставляем старую строку
            getCandyHub().captureException(e);
        }
    };

    const onChangeHandler = (currentText: string) => {
        if (currentText.slice(-1) !== " " || currentText === "") {
            onValueChange(convertFiasAddressToModelAddress({}, extendedFields));
        }
        setStringAddress(currentText);
    };

    const openModalHandler = () => {
        setModalEditorOpened(true);
    };

    const setIsInputFocused = (state: boolean) => {
        setIsInFocus(state);
    };

    useTestPageObject(
        () => FiasTestPageObject(value, setMode, onChangeHandler, fiasInputMethods),
        [value, fiasInputMethods]
    );

    if (isReadOnly) {
        return <Text value={stringAddress} bindingPath={emptyAbsoluteModelPath()} onClick={onClick} isLink={isLink} />;
    }

    const setTestMethodInPageObject = (methods: FiasTestPageObjectType) => {
        setFiasInputMethods(methods);
    };

    const trackModalAddressSaved = (fiasAddress: FiasAddress) => {
        runFloatingAsyncAction(async () => {
            const address = await extractStringAddress(fiasAddress, requiredExtendedFields, apiClient.current);
            try {
                const exists = await checkAddressExists(
                    address,
                    extendedFields,
                    requiredExtendedFields,
                    apiClient.current
                );
                track(FIAS_SAVE_MODAL_CUSTOM_ADDRESS, { exists: exists });
            } catch {
                track(FIAS_SAVE_MODAL_CUSTOM_ADDRESS, { exists: false });
            }
        });
    };

    if (props.adrGarOptions?.extendModal) {
        return (
            <FiasWidget
                apiUrl={apiClient.current.getApiUrl()}
                division={division}
                fiasSchema={props.adrGarOptions.fiasSchema}
                countryCode={props.adrGarOptions.countryCode}
                countrySelector={props.adrGarOptions.countrySelector}
                divisionSelector={props.adrGarOptions.divisionSelector}
                viewMode={props.adrGarOptions.viewMode}
                menuAlign={props.adrGarOptions.menuAlign}
                bindingPath={bindingPath}
                stringAddress={stringAddress}
                value={value}
                extendedFields={extendedFields}
                onValueChange={onSaveViaWidget}
                apiClient={apiClient.current}
            />
        );
    }

    return (
        <Root ref={container}>
            {mode === "show" ? (
                <FiasStringAddress stringAddress={stringAddress} onRequestFocus={setEditModeCallback} />
            ) : (
                <FiasInput
                    bindingPath={bindingPath}
                    apiClient={apiClient.current}
                    error={error}
                    warning={warning}
                    focusByEnterOnNextControl={focusByEnterOnNextControl}
                    withSteads={withSteads}
                    stringAddress={stringAddress}
                    submitAddress={submitHandler}
                    onStringAddressChange={onChangeHandler}
                    Tid={"FiasAddress"}
                    onBlur={onBlurHandler}
                    openModalHandler={openModalHandler}
                    setFocus={focusOnOpen}
                    onlyInput={onlyInput}
                    setIsInputFocused={setIsInputFocused}
                    setTestMethodInPageObject={setTestMethodInPageObject}
                    hasOwnBindingPath={hasOwnBindingPath}
                    extendedFields={extendedFields}
                />
            )}
            {mode === "show" && (
                <Link
                    style={{ display: "block" }}
                    data-tid="FiasOpenModal"
                    onClick={setEditModeCallback}
                    icon={<ToolPencilLineIcon />}>
                    {stringAddress ? "Редактировать" : "Заполнить"}
                </Link>
            )}
            {internalWarning && !onlyInput && <WarningText>{internalWarning}</WarningText>}

            {modalEditorOpened && (
                <FiasModalEditor
                    apiClient={apiClient.current}
                    extendedFields={extendedFields}
                    withSteads={withSteads}
                    withForeignCountry={withForeignCountry}
                    title={title}
                    focusByEnterOnNextControl={focusByEnterOnNextControl}
                    stringAddress={stringAddress}
                    value={value}
                    onApply={onApplyHandler}
                    onDiscard={handleCloseModalEditor}
                    fnsOptions={fnsOptions}
                    mvdOptions={mvdOptions}
                />
            )}
        </Root>
    );
}
